import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchStrategyHistory } from "@/state/slices/strategiesSlice";

/** Closed trades of the selected strategies, for the Strategies chart's
 *  entry/exit markers. Only while the view is open. Refetches a strategy when
 *  its open-position count drops (a close adds a trade to its history). */
export function useStrategyHistoryAutoFetch(): void {
  const dispatch = useAppDispatch();
  const active = useAppSelector((s) => s.strategies.viewOpen && s.strategies.enabled);
  const selected = useAppSelector((s) => s.strategies.selectedIds);
  const running = useAppSelector((s) =>
    s.strategies.selectedIds
      .map((id) => `${id}:${s.strategies.book[id]?.positions.length ?? 0}`)
      .join(","),
  );
  const prevRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!active) return;
    for (const id of selected) void dispatch(fetchStrategyHistory(id));
  }, [dispatch, active, selected]);

  useEffect(() => {
    if (!active) return;
    const next: Record<string, number> = {};
    for (const part of running ? running.split(",") : []) {
      const [id, n] = part.split(":");
      next[id] = Number(n);
      const prev = prevRef.current[id];
      if (prev != null && next[id] < prev) void dispatch(fetchStrategyHistory(id));
    }
    prevRef.current = next;
  }, [dispatch, active, running]);
}
